// @spec ANL-STR-013
// @spec ANL-STR-017
// @spec ANL-STR-019
'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';

import type { DashboardResponse } from '@/features/analytics/custom-dashboards';
import {
  CreateDashboardDialog,
  DashboardList,
  DeleteDashboardDialog,
  ShareDashboardDialog,
  useCreateDashboard,
  useDashboardCount,
  useDashboards,
  useDeleteDashboard,
} from '@/features/analytics/custom-dashboards';
import { useActiveOrganizationId } from '@/hooks/use-active-org-id';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/analytics/dashboards/')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.myDashboards'),
  }),
  component: DashboardsPage,
});

/**
 * Dashboard list page for viewing and managing custom dashboards.
 */
function DashboardsPage() {
  const { t } = useTranslation('analytics');
  const navigate = useNavigate();
  const { organizationId: orgId } = useActiveOrganizationId();

  // State
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [duplicateFrom, setDuplicateFrom] = useState<DashboardResponse | undefined>();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dashboardToDelete, setDashboardToDelete] = useState<DashboardResponse | undefined>();
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [dashboardToShare, setDashboardToShare] = useState<DashboardResponse | undefined>();

  // Queries
  const { data: dashboardsData, isLoading } = useDashboards(orgId);
  const { data: countData } = useDashboardCount(orgId);

  // Mutations
  const createMutation = useCreateDashboard(orgId, {
    onSuccess: (dashboard) => {
      toast.success(t('analytics:toast.dashboardCreated'));
      void navigate({
        to: '/analytics/dashboards/$dashboardId',
        params: { dashboardId: dashboard.id },
      });
    },
    onError: (error) => {
      const message = (error as { message?: string }).message ?? 'Failed to create dashboard';
      toast.error(message);
    },
  });

  const deleteMutation = useDeleteDashboard(orgId, {
    onSuccess: () => {
      toast.success(t('analytics:toast.dashboardDeleted'));
      setDeleteDialogOpen(false);
      setDashboardToDelete(undefined);
    },
    onError: (error) => {
      const message = (error as { message?: string }).message ?? 'Failed to delete dashboard';
      toast.error(message);
    },
  });

  // Handlers
  const handleSelect = (dashboard: DashboardResponse) => {
    void navigate({
      to: '/analytics/dashboards/$dashboardId',
      params: { dashboardId: dashboard.id },
    });
  };

  const handleCreate = () => {
    setDuplicateFrom(undefined);
    setCreateDialogOpen(true);
  };

  const handleDuplicate = (dashboard: DashboardResponse) => {
    setDuplicateFrom(dashboard);
    setCreateDialogOpen(true);
  };

  const handleDelete = (dashboard: DashboardResponse) => {
    setDashboardToDelete(dashboard);
    setDeleteDialogOpen(true);
  };

  const handleShare = (dashboard: DashboardResponse) => {
    setDashboardToShare(dashboard);
    setShareDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (dashboardToDelete) {
      deleteMutation.mutate(dashboardToDelete.id);
    }
  };

  const handleCreateSubmit = async (data: {
    name: string;
    description?: string | undefined;
    duplicateFromId?: string | undefined;
  }) => {
    if (!orgId) {
      toast.error(t('analytics:toast.organizationNotLoadedYetPleaseTryAgain'));
      return;
    }
    await createMutation.mutateAsync(data);
  };

  return (
    <div className="min-h-screen bg-background p-6 lg:p-8">
      <div className="mx-auto max-w-7xl">
        {/*
          TODO(WP4-B): Render <GroupScopePill /> adjacent to the dashboard
          list title. Custom analytics widgets already return
          `WidgetDataResponse` with `RollupScopeMixin` fields, but the
          dashboards list endpoint does not yet surface aggregated scope
          metadata. See `docs/subsidiary-refactor/MIGRATION-GUIDE.md`.
        */}
        <DashboardList
          dashboards={dashboardsData?.items ?? []}
          isLoading={isLoading}
          onSelect={handleSelect}
          onDuplicate={handleDuplicate}
          onDelete={handleDelete}
          onShare={handleShare}
          onCreate={handleCreate}
          dashboardCount={countData?.count}
          dashboardLimit={countData?.limit}
        />
      </div>

      {/* Create Dashboard Dialog */}
      <CreateDashboardDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        duplicateFrom={duplicateFrom}
        onSubmit={handleCreateSubmit}
        isSubmitting={createMutation.isPending}
        dashboardCount={countData?.count}
        dashboardLimit={countData?.limit}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteDashboardDialog
        isOpen={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        dashboard={dashboardToDelete ?? null}
        onConfirm={handleConfirmDelete}
        isDeleting={deleteMutation.isPending}
      />

      {/* Share Dashboard Dialog */}
      {dashboardToShare && orgId && (
        <ShareDashboardDialog
          dashboardId={dashboardToShare.id}
          dashboardName={dashboardToShare.name}
          isOpen={shareDialogOpen}
          onClose={() => {
            setShareDialogOpen(false);
            setDashboardToShare(undefined);
          }}
          currentVisibility={dashboardToShare.visibility}
          orgId={orgId}
          isOwner={true}
        />
      )}
    </div>
  );
}
